/**
 * Dang nhap, dang ky va xac minh kenh lien lac.
 *
 * Diem can chu y ve nghiep vu: may chu KHONG tiet lo ly do cu the khi tai khoan
 * bi chan hay dang tu loai tru (PLR-R26) — giao dien phai ton trong dieu do va
 * khong duoc doan them, vi doan sai la lo thong tin cua nguoi khac.
 */

import { useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, deviceFingerprint } from '../lib/session';
import { Button, Field, ErrorList } from '../components/ui';
import styles from './Auth.module.css';

function useApiErrors(): [string[], (err: unknown) => void, () => void] {
  const [messages, setMessages] = useState<string[]>([]);
  const capture = (err: unknown): void => {
    if (err instanceof ApiError) setMessages(err.messages);
    else setMessages([err instanceof Error ? err.message : 'Đã xảy ra lỗi, vui lòng thử lại.']);
  };
  return [messages, capture, () => setMessages([])];
}

// ---------------------------------------------------------------------------

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { login } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, captureError, clearErrors] = useApiErrors();

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    clearErrors();
    setBusy(true);
    try {
      await login(identifier, password);
      navigate('/');
    } catch (err) {
      captureError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>
        <h1 className={styles.title}>Đăng nhập</h1>

        <form onSubmit={submit} className={styles.form} noValidate>
          <ErrorList messages={errors} />

          <Field
            label="Tên đăng nhập hoặc email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            required
          />
          <Field
            label="Mật khẩu"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <Button type="submit" loading={busy} fullWidth size="lg">
            Đăng nhập
          </Button>
        </form>

        <p className={styles.switch}>
          Chưa có tài khoản? <Link to="/dang-ky" className={styles.link}>Đăng ký</Link>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    date_of_birth: '',
    country_code: 'US',
    currency: 'USD',
    affiliate_code: '',
  });
  const [busy, setBusy] = useState(false);
  const [errors, captureError, clearErrors] = useApiErrors();

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    clearErrors();
    setBusy(true);
    try {
      const result = await api.auth.register({
        username: form.username,
        email: form.email,
        password: form.password,
        date_of_birth: form.date_of_birth,
        country_code: form.country_code,
        currency: form.currency,
        terms_version: 'v1',
        device_fingerprint: deviceFingerprint(),
        ...(form.affiliate_code ? { affiliate_code: form.affiliate_code } : {}),
      });
      navigate(`/xac-minh?player=${result.player_id}`, {
        // Chi co o moi truong phat trien; moi truong that gui ma qua email hoac SMS.
        state: { hint: result.verification_code },
      });
    } catch (err) {
      captureError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>
        <h1 className={styles.title}>Tạo tài khoản</h1>

        <form onSubmit={submit} className={styles.form} noValidate>
          <ErrorList messages={errors} />

          <Field
            label="Tên đăng nhập"
            value={form.username}
            onChange={set('username')}
            hint="6 đến 20 ký tự, chỉ chữ và số"
            autoComplete="username"
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={set('email')}
            autoComplete="email"
            required
          />
          <Field
            label="Mật khẩu"
            type="password"
            value={form.password}
            onChange={set('password')}
            hint="Tối thiểu 10 ký tự, có cả chữ và số"
            autoComplete="new-password"
            required
          />
          <Field
            label="Ngày sinh"
            type="date"
            value={form.date_of_birth}
            onChange={set('date_of_birth')}
            hint="Phải đủ tuổi tối thiểu theo quy định nơi cấp phép"
            required
          />

          <div className={styles.row}>
            <Field
              label="Quốc gia"
              value={form.country_code}
              onChange={set('country_code')}
              maxLength={2}
              required
            />
            <Field
              label="Tiền tệ"
              value={form.currency}
              onChange={set('currency')}
              hint="Không đổi được sau giao dịch đầu tiên"
              maxLength={4}
              required
            />
          </div>

          <Field
            label="Mã giới thiệu"
            value={form.affiliate_code}
            onChange={set('affiliate_code')}
            hint="Không bắt buộc"
          />

          <Button type="submit" loading={busy} fullWidth size="lg">
            Tạo tài khoản
          </Button>

          <p className={styles.legal}>
            Tạo tài khoản đồng nghĩa với việc chấp nhận điều khoản sử dụng và xác nhận đã đủ tuổi
            theo quy định.
          </p>
        </form>

        <p className={styles.switch}>
          Đã có tài khoản? <Link to="/dang-nhap" className={styles.link}>Đăng nhập</Link>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function VerifyPage(): JSX.Element {
  const navigate = useNavigate();
  const playerId = new URLSearchParams(window.location.search).get('player') ?? '';
  const hint = (window.history.state?.usr as { hint?: string } | undefined)?.hint;

  const [code, setCode] = useState(hint ?? '');
  const [busy, setBusy] = useState(false);
  const [errors, captureError, clearErrors] = useApiErrors();

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    clearErrors();
    setBusy(true);
    try {
      await api.auth.verify(playerId, code);
      navigate('/dang-nhap');
    } catch (err) {
      captureError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>
        <h1 className={styles.title}>Xác minh tài khoản</h1>
        <p className={styles.lede}>Nhập mã xác minh vừa được gửi tới địa chỉ email đã đăng ký.</p>

        <form onSubmit={submit} className={styles.form} noValidate>
          <ErrorList messages={errors} />
          <Field
            label="Mã xác minh"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            maxLength={6}
            required
          />
          <Button type="submit" loading={busy} fullWidth size="lg">
            Xác minh
          </Button>
        </form>
      </div>
    </div>
  );
}
